import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "~/common/mui";
import type { PackagingOption } from "~/domains/product/types/PackagingOption";
import { getStockLevel, StockIndicatorIcon } from "~/domains/product/components/StockIndicator/StockIndicator";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { CollapsibleSection, CollapsedTitle } from "../shared/CollapsibleSection";
import { StockTable } from "../shared/StockTable";
import { SectionTitle } from "../shared/styled";

import { useAvailabilityEntries } from "../shared/useAvailabilityEntries";

// ── Formatter ──────────────────────────────────────────────────────────────

const RESULT_NF = NumberFormatter.getFormatter();

// ── useInStockCalculations ─────────────────────────────────────────────────

export const useInStockCalculations = (pack: PackagingOption) => {
  const { transform } = useAvailabilityEntries();

  return useMemo(() => {
    const inStockEntries = transform(pack.stockLevels.inStock, pack.stockLevels.inStockWeekly);
    const regularInStockEntries = inStockEntries.filter((entry) => !entry.sale);
    const totalInStock = regularInStockEntries.reduce((acc, entry) => acc + entry.quantity, 0);

    return { inStockEntries, totalInStock, firstRegularStockEntry: regularInStockEntries[0] };
  }, [pack, transform]);
};

// ── StockIndicatorValue ────────────────────────────────────────────────────

export const StockIndicatorValue = ({ totalInStock, as }: { totalInStock: number; as?: React.ElementType }) => {
  const { t } = useTranslation();
  const Component = as ?? "p";

  return (
    <Stack direction={"row"} gap={1} sx={{ alignItems: "center" }}>
      <StockIndicatorIcon level={getStockLevel(totalInStock)} />
      <Component>
        {totalInStock > 0 &&
          t("stockIndicator.existsWithQty", { amount: NumberFormatter.getDefaultFormatter().format(totalInStock) })}
        {totalInStock === 0 && t("stockIndicator.currentlyNotInStock")}
      </Component>
    </Stack>
  );
};

// ── InStockSection ─────────────────────────────────────────────────────────

interface InStockSectionProps {
  pack: PackagingOption;
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
  totalInStock: number;
  firstRegularStockEntry: { quantity: number; date: string } | undefined;
  inStockEntries: { quantity: number; date: string; sale?: true; soldOut?: true }[];
}

export const InStockSection = ({
  pack,
  expanded,
  onToggle,
  showToggle = true,
  totalInStock,
  firstRegularStockEntry,
  inStockEntries,
}: InStockSectionProps) => {
  const { t } = useTranslation();
  const canExpand = totalInStock > 0;
  const quantity = firstRegularStockEntry ? Number(firstRegularStockEntry.quantity) : 0;

  return (
    <CollapsibleSection
      expanded={expanded && canExpand}
      onToggle={onToggle}
      showToggle={showToggle && canExpand}
      canExpand={canExpand}
      title={
        <>
          <StockIndicatorValue as={SectionTitle} totalInStock={totalInStock} />
          {!expanded && firstRegularStockEntry && canExpand && (
            <CollapsedTitle>
              {t("productDetailsPage.availabilityTable.upcoming", {
                quantity: isNaN(quantity) ? firstRegularStockEntry.quantity : RESULT_NF.format(quantity),
                date: firstRegularStockEntry.date,
              })}
            </CollapsedTitle>
          )}
        </>
      }
    >
      <StockTable warehouseName={pack.stockLevels.warehouseName} stock={inStockEntries} />
    </CollapsibleSection>
  );
};
